/**
 * A stale page learns a new build has landed before it tries to do work with
 * an old client. It asks the deployed build-id.json, never a guess, and
 * offers to reload only when the two ids disagree.
 *
 * Owner, 2026-09-29: back to this plain notice — "A new version of Brunel is
 * available · Reload" — and only the person reloads. The #856 countdown,
 * automatic reload and typing hold are gone. What a page saves on the way out
 * (the open main command, the notepad) is still saved when Reload is pressed;
 * it never delays or blocks the reload.
 */
import { runReloadPreservers } from './safe-reload';

declare const __BRUNEL_BUILD_ID__: string;

const CHECK_INTERVAL_MS = 60_000;

export function startVersionCheck(): void {
  const buildId = typeof __BRUNEL_BUILD_ID__ === 'string' ? __BRUNEL_BUILD_ID__ : 'dev';
  if (buildId === 'dev') return;

  const showReload = (): void => {
    if (document.querySelector('[data-version-popup]')) return;
    const popup = document.createElement('div');
    popup.setAttribute('data-version-popup', 'true');
    popup.setAttribute('role', 'status');
    popup.className = 'version-popup';

    const sentence = document.createElement('span');
    sentence.textContent = 'A new version of Brunel is available.';
    popup.appendChild(sentence);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'primary';
    button.textContent = 'Reload';
    button.addEventListener('click', () => {
      runReloadPreservers();
      window.location.reload();
    });
    popup.appendChild(button);

    document.body.appendChild(popup);
  };

  const check = async (): Promise<void> => {
    try {
      const response = await fetch(`/build-id.json?ts=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return;
      const parsed = (await response.json()) as { id?: unknown };
      if (typeof parsed.id === 'string' && parsed.id !== buildId) showReload();
    } catch {
      // A deploy that cannot be read is not a reason to break the page.
    }
  };

  void check();
  window.setInterval(() => { void check(); }, CHECK_INTERVAL_MS);
}
